import React, { useMemo, useState } from 'react';
import {
  Button,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Tag,
  Upload
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  cancelAfterSales,
  completeAfterSales,
  confirmAfterSalesPrice,
  createAfterSales,
  submitAfterSalesReturn
} from '../services/afterSalesService';
import { uploadApprovalAttachmentFile } from '../services/api';
import {
  AFTER_SALES_STATUS_TEXT,
  AfterSalesRequest,
  AfterSalesStatus
} from '../types/afterSales';

const STATUS_COLOR: Partial<Record<AfterSalesStatus, string>> = {
  pending_merchant_review: 'orange',
  pending_team_price_confirmation: 'gold',
  pending_user_return: 'blue',
  pending_merchant_fulfillment: 'purple',
  pending_team_completion: 'cyan',
  completed: 'green',
  merchant_rejected: 'red',
  cancelled: 'default'
};

interface Props {
  applicationId: string;
  projectIndex: number;
  category: '耗材申请' | '试剂申请';
  itemName: string;
  originalQuantity: number;
  eligible: boolean;
  isOwner: boolean;
  canManage: boolean;
  requests: AfterSalesRequest[];
  onChanged: () => void;
}

const money = (value?: number | null) =>
  value == null ? '—' : `¥${Number(value).toFixed(2)}`;

export default function AfterSalesEntry({
  applicationId,
  projectIndex,
  category,
  itemName,
  originalQuantity,
  eligible,
  isOwner,
  canManage,
  requests,
  onChanged
}: Props) {
  const [open, setOpen] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [type, setType] = useState<'return' | 'exchange'>('return');
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const [actionForm] = Form.useForm();
  const latest = requests[0];
  const usedQuantity = useMemo(
    () =>
      requests
        .filter((row) => !['merchant_rejected', 'cancelled'].includes(row.status))
        .reduce((sum, row) => sum + Number(row.requestedQuantity || 0), 0),
    [requests]
  );
  const remainingQuantity = Math.max(0, originalQuantity - usedQuantity);

  const close = () => {
    setOpen(false);
    setCreateMode(false);
    form.resetFields();
    actionForm.resetFields();
    setFiles([]);
  };

  const refresh = async () => {
    await onChanged();
    setCreateMode(false);
    form.resetFields();
    setFiles([]);
  };

  const submitCreate = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await createAfterSales({
        applicationId,
        projectIndex,
        type,
        requestedQuantity: values.requestedQuantity,
        reason: values.reason,
        description: values.description,
        opened: values.opened === 'yes',
        affectsResale: values.affectsResale === 'yes',
        refundMethod: type === 'return' ? values.refundMethod : undefined,
        expectedRefundAmount: type === 'return' ? values.expectedRefundAmount : undefined,
        evidenceUrls: files.map((file) => file.url).filter(Boolean),
        replacementItem:
          type === 'exchange'
            ? {
                name: values.replacementName,
                brand: values.replacementBrand,
                specification: values.replacementSpecification,
                cas: values.replacementCas,
                catalogNumber: values.replacementCatalogNumber,
                amount: values.requestedQuantity,
                unit: values.replacementUnit
              }
            : undefined
      });
      message.success('售后申请已提交');
      await refresh();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const performAction = async (action: 'approve' | 'rejectPrice' | 'ship' | 'complete' | 'cancel') => {
    if (!latest) return;
    try {
      const values = await actionForm.validateFields();
      setSubmitting(true);
      if (action === 'approve') {
        await confirmAfterSalesPrice(latest._id, true, values.comment);
        message.success('已确认售后金额');
      } else if (action === 'rejectPrice') {
        await confirmAfterSalesPrice(latest._id, false, values.comment);
        message.success('已退回商家重新报价');
      } else if (action === 'ship') {
        await submitAfterSalesReturn(latest._id, values.trackingNumber);
        message.success('退货物流已提交');
      } else if (action === 'complete') {
        await completeAfterSales(latest._id);
        window.dispatchEvent(
          new CustomEvent(category === '试剂申请' ? 'reagentStockUpdated' : 'consumableStockUpdated')
        );
        message.success('售后已确认完成');
      } else {
        await cancelAfterSales(latest._id);
        message.success('售后申请已撤销');
      }
      actionForm.resetFields();
      await refresh();
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canCreate = eligible && isOwner && remainingQuantity > 0;
  if (!latest && !canCreate) return <span style={{ color: '#999' }}>—</span>;

  return (
    <>
      <Button
        size="small"
        onClick={() => {
          setOpen(true);
          setCreateMode(!latest);
        }}
      >
        {latest ? '售后处理' : '申请售后'}
      </Button>
      <Modal
        title={`${itemName || '商品'} · 售后处理`}
        open={open}
        onCancel={close}
        width={720}
        footer={
          createMode
            ? [
                <Button key="cancel" onClick={() => (latest ? setCreateMode(false) : close())}>
                  取消
                </Button>,
                <Button key="submit" type="primary" loading={submitting} onClick={submitCreate}>
                  提交售后申请
                </Button>
              ]
            : [<Button key="close" onClick={close}>关闭</Button>]
        }
      >
        {createMode ? (
          <Form
            form={form}
            layout="vertical"
            initialValues={{ requestedQuantity: 1, reason: '质量问题', refundMethod: 'original' }}
          >
            <Form.Item label="售后类型">
              <Select
                value={type}
                onChange={setType}
                options={[
                  { label: '退货退款', value: 'return' },
                  { label: '换货', value: 'exchange' }
                ]}
              />
            </Form.Item>
            <Form.Item
              name="requestedQuantity"
              label={`售后数量（剩余可申请 ${remainingQuantity}）`}
              rules={[{ required: true, message: '请填写售后数量' }]}
            >
              <InputNumber min={0.000001} max={remainingQuantity} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="reason" label="售后原因" rules={[{ required: true }]}>
              <Select
                options={[
                  '质量问题',
                  '规格/货号不符',
                  '破损或泄漏',
                  '少货',
                  '临期/过期',
                  '不再需要',
                  '其他'
                ].map((value) => ({ label: value, value }))}
              />
            </Form.Item>
            <Form.Item name="description" label="问题说明">
              <Input.TextArea rows={3} maxLength={1000} showCount />
            </Form.Item>
            <Space size="large">
              <Form.Item name="opened" label="是否已拆封">
                <Select
                  style={{ width: 130 }}
                  options={[
                    { label: '否', value: 'no' },
                    { label: '是', value: 'yes' }
                  ]}
                />
              </Form.Item>
              <Form.Item name="affectsResale" label="是否影响二次销售">
                <Select
                  style={{ width: 160 }}
                  options={[
                    { label: '否', value: 'no' },
                    { label: '是', value: 'yes' }
                  ]}
                />
              </Form.Item>
            </Space>
            <Form.Item label="图片/视频凭证（最多 9 个）">
              <Upload
                fileList={files}
                maxCount={9}
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    const result = await uploadApprovalAttachmentFile(file as File);
                    if (!result.success || !result.data?.url) throw new Error(result.error || '上传失败');
                    setFiles((current) => [
                      ...current,
                      {
                        uid: `${Date.now()}-${(file as File).name}`,
                        name: (file as File).name,
                        status: 'done',
                        url: result.data!.url
                      }
                    ]);
                    onSuccess?.(result);
                  } catch (error) {
                    onError?.(error as Error);
                    message.error(error instanceof Error ? error.message : '上传失败');
                  }
                }}
                onRemove={(file) => setFiles((current) => current.filter((row) => row.uid !== file.uid))}
                showUploadList
              >
                <Button icon={<UploadOutlined />}>上传凭证</Button>
              </Upload>
            </Form.Item>
            {type === 'return' ? (
              <>
                <Form.Item name="refundMethod" label="退款方式" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { label: '原支付渠道', value: 'original' },
                      { label: '团队余额', value: 'team_balance' }
                    ]}
                  />
                </Form.Item>
                <Form.Item name="expectedRefundAmount" label="期望退款金额">
                  <InputNumber min={0} precision={2} prefix="¥" style={{ width: '100%' }} />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item name="replacementName" label="换货商品名称" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Space size="middle" wrap>
                  <Form.Item name="replacementBrand" label="品牌">
                    <Input style={{ width: 180 }} />
                  </Form.Item>
                  <Form.Item name="replacementSpecification" label="规格/型号" rules={[{ required: true }]}>
                    <Input style={{ width: 180 }} />
                  </Form.Item>
                  {category === '试剂申请' && (
                    <>
                      <Form.Item name="replacementCas" label="CAS号">
                        <Input style={{ width: 180 }} />
                      </Form.Item>
                      <Form.Item name="replacementCatalogNumber" label="货号">
                        <Input style={{ width: 180 }} />
                      </Form.Item>
                    </>
                  )}
                  <Form.Item name="replacementUnit" label="单位">
                    <Input style={{ width: 120 }} />
                  </Form.Item>
                </Space>
              </>
            )}
          </Form>
        ) : latest ? (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="状态" span={2}>
                <Tag color={STATUS_COLOR[latest.status]}>{AFTER_SALES_STATUS_TEXT[latest.status]}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="类型">{latest.type === 'return' ? '退货退款' : '换货'}</Descriptions.Item>
              <Descriptions.Item label="数量">{latest.requestedQuantity}</Descriptions.Item>
              <Descriptions.Item label="原因">{latest.reason}</Descriptions.Item>
              <Descriptions.Item label="原商品售后价值">{money(latest.originalReturnValue)}</Descriptions.Item>
              <Descriptions.Item label="商家确认退款">
                {money(latest.merchantConfirmedAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="换货报价">{money(latest.replacementQuote)}</Descriptions.Item>
              <Descriptions.Item label="换货差价">{money(latest.priceDifference)}</Descriptions.Item>
              <Descriptions.Item label="退货物流">{latest.returnTrackingNumber || '—'}</Descriptions.Item>
              <Descriptions.Item label="换货物流">
                {latest.merchantOutboundTrackingNumber || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="商家说明" span={2}>
                {latest.merchantDecisionNote || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="库存同步" span={2}>
                {latest.inventoryAppliedAt
                  ? latest.type === 'return'
                    ? '已扣减原库存'
                    : '已扣减原库存并生成换货库存'
                  : latest.status === 'completed'
                    ? '完成时尚未入库，未调整库存'
                    : '待售后完成后同步'}
              </Descriptions.Item>
              <Descriptions.Item label="问题说明" span={2}>
                {latest.description || '—'}
              </Descriptions.Item>
            </Descriptions>
            <Form form={actionForm} layout="vertical" style={{ marginTop: 16 }}>
              {canManage && latest.status === 'pending_team_price_confirmation' && (
                <>
                  <Form.Item name="comment" label="团队金额确认意见">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                  <Space>
                    <Button type="primary" loading={submitting} onClick={() => performAction('approve')}>
                      确认金额
                    </Button>
                    <Button danger loading={submitting} onClick={() => performAction('rejectPrice')}>
                      退回报价
                    </Button>
                  </Space>
                </>
              )}
              {isOwner && latest.status === 'pending_user_return' && (
                <Space align="end">
                  <Form.Item
                    name="trackingNumber"
                    label="退货物流单号"
                    rules={[{ required: true, message: '请填写物流单号' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input style={{ width: 300 }} />
                  </Form.Item>
                  <Button type="primary" loading={submitting} onClick={() => performAction('ship')}>
                    确认寄出
                  </Button>
                </Space>
              )}
              {canManage && latest.status === 'pending_team_completion' && (
                <Button type="primary" loading={submitting} onClick={() => performAction('complete')}>
                  确认售后完成
                </Button>
              )}
              {isOwner && latest.status === 'pending_merchant_review' && (
                <Button danger loading={submitting} onClick={() => performAction('cancel')}>
                  撤销售后
                </Button>
              )}
              {canCreate && ['completed', 'merchant_rejected', 'cancelled'].includes(latest.status) && (
                <Button onClick={() => setCreateMode(true)}>再次申请售后</Button>
              )}
            </Form>
          </>
        ) : null}
      </Modal>
    </>
  );
}
